import React from 'react';
import { Scale, TrendingDown, TrendingUp, Minus, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { LOG_CARD_CLASS } from './FoodLogCard';

function getWeightVerdict(delta, goal) {
  if (delta == null || !goal) return null;
  const g = String(goal).toLowerCase();
  if (g.includes('lose') || g.includes('חיטוב') || g.includes('ירידה')) {
    if (delta < -0.2) return 'on';
    if (delta > 0.2) return 'off';
    return 'holding';
  }
  if (g.includes('gain') || g.includes('מסה') || g.includes('עליה')) {
    if (delta > 0.2) return 'on';
    if (delta < -0.2) return 'off';
    return 'holding';
  }
  // Default to maintain
  if (Math.abs(delta) <= 0.5) return 'on';
  return 'off';
}

function WeightVerdictPill({ delta, goal, language }) {
  const verdict = getWeightVerdict(delta, goal);
  if (!verdict) return null;
  const he = language === 'hebrew';
  
  let label = '';
  let colorClass = '';
  let Icon = null;
  
  if (verdict === 'on') {
    label = he ? 'בתוכנית' : 'On Plan';
    colorClass = 'bg-[color:var(--progress)]/10 text-[color:var(--progress)]';
    Icon = CheckCircle2;
  } else if (verdict === 'holding') {
    label = he ? 'ללא שינוי' : 'Holding';
    colorClass = 'bg-moss dark:bg-moss-dark text-ink-muted dark:text-ink-muted-dark';
    Icon = Minus;
  } else {
    label = he ? 'חריגה' : 'Off Plan';
    colorClass = 'bg-[color:var(--danger-bg)] text-[color:var(--danger-text)]';
    Icon = AlertTriangle;
  }
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${colorClass}`}>
      <Icon size={12} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export default function WeightLogCard({ event, language, timeLabel, clientTargets, previousWeight }) {
  const log = event.payload;
  const he = language === 'hebrew';
  const weight = log.weight_kg != null ? Number(log.weight_kg) : null;
  const bf = log.body_fat_percentage != null ? Number(log.body_fat_percentage) : null;
  const notes = log.notes ? String(log.notes).trim() : '';
  
  const delta = (weight != null && previousWeight != null) ? weight - previousWeight : null;

  return (
    <div className="flex w-full justify-center">
      <div
        className={`${LOG_CARD_CLASS} min-h-[44px] p-3 flex gap-3`}
        role="article"
        aria-label={he ? 'רישום משקל' : 'Weight log'}
      >
        <div className="w-14 h-14 rounded-md flex-shrink-0 bg-moss dark:bg-moss-dark flex items-center justify-center">
          <Scale size={22} className="text-forest-ondark" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 py-0.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[14px] font-medium text-ink dark:text-ink-dark">
              {he ? 'שקילה' : 'Weigh-in'}
            </span>
            <span className="text-[11px] text-ink-muted dark:text-ink-muted-dark tabular-nums flex-shrink-0">{timeLabel}</span>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[14px] font-semibold text-ink dark:text-ink-dark tabular-nums">
              {weight != null && Number.isFinite(weight) ? `${weight.toFixed(1)} ${he ? 'ק״ג' : 'kg'}` : '—'}
            </span>
            
            {delta != null && delta !== 0 && (() => {
              const verdict = getWeightVerdict(delta, clientTargets?.goal);
              const isGood = verdict === 'on';
              const isBad = verdict === 'off';
              const colorClass = isGood ? 'text-[color:var(--forest-ondark)]' : (isBad ? 'text-[color:var(--danger-text)]' : 'text-ink-muted dark:text-ink-muted-dark');
              return (
                <span className={`inline-flex items-center gap-0.5 text-[12px] font-medium tabular-nums ${colorClass}`}>
                  {delta > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {Math.abs(delta).toFixed(1)} {he ? 'ק״ג מרישום קודם' : 'kg vs prev'}
                </span>
              );
            })()}
            
            <WeightVerdictPill delta={delta} goal={clientTargets?.goal} language={language} />
          </div>
          
          {(bf != null || notes) && (
            <div className="flex items-center gap-2 text-[12px] mt-1 pt-1 border-t border-line dark:border-line-dark">
              {bf != null && Number.isFinite(bf) && (
                <span className="text-ink-muted dark:text-ink-muted-dark tabular-nums whitespace-nowrap">
                  {he ? 'שומן' : 'Body fat'}: {bf.toFixed(1)}%
                </span>
              )}
              {notes && (
                <span className="text-ink dark:text-ink-dark truncate" title={notes}>
                  {bf != null && <span className="opacity-30 mx-1">•</span>}
                  {notes}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
